
import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { gsap } from 'gsap';

export const LoadingSpinner3D = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Set up scene
    const container = containerRef.current;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });

    // Make size responsive
    const updateSize = () => {
      const width = Math.min(400, window.innerWidth * 0.8);
      const height = Math.min(300, window.innerHeight * 0.4);
      renderer.setSize(width, height);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    };

    updateSize();
    container.appendChild(renderer.domElement);
    window.addEventListener('resize', updateSize);

    // Create globe
    const globeGeometry = new THREE.SphereGeometry(2, 32, 32);
    const globeMaterial = new THREE.MeshPhongMaterial({
      color: 0x1a1a1a,
      transparent: true,
      opacity: 0.8,
    });
    const globe = new THREE.Mesh(globeGeometry, globeMaterial);
    scene.add(globe);

    // Create neuron connections
    const neuronCount = 200;
    const neurons = new THREE.Group();
    
    for (let i = 0; i < neuronCount; i++) {
      const phi = Math.acos(-1 + (2 * i) / neuronCount);
      const theta = Math.sqrt(neuronCount * Math.PI) * phi;

      const neuronGeometry = new THREE.SphereGeometry(0.03, 8, 8);
      const neuronMaterial = new THREE.MeshPhongMaterial({
        color: 0x00ff88,
        emissive: 0x00ff88,
        emissiveIntensity: 0.5,
      });
      
      const neuron = new THREE.Mesh(neuronGeometry, neuronMaterial);
      
      neuron.position.setFromSphericalCoords(
        2,
        phi,
        theta
      );
      
      neurons.add(neuron);

      // Add random connections between neurons
      if (i > 0 && Math.random() > 0.7) {
        const previousNeuron = neurons.children[Math.floor(Math.random() * i)];
        const connectionGeometry = new THREE.BufferGeometry().setFromPoints([
          neuron.position,
          previousNeuron.position
        ]);
        const connectionMaterial = new THREE.LineBasicMaterial({
          color: 0x00ff88,
          transparent: true,
          opacity: 0.3,
        });
        const connection = new THREE.Line(connectionGeometry, connectionMaterial);
        neurons.add(connection);
      }
    }
    
    scene.add(neurons);

    // Add lights
    const ambientLight = new THREE.AmbientLight(0x404040);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0x00ff88, 1);
    directionalLight.position.set(5, 5, 5);
    scene.add(directionalLight);

    // Position camera
    camera.position.z = 6;

    // Animations
    let frame = 0;
    gsap.to(globe.rotation, {
      y: Math.PI * 2,
      duration: 8,
      repeat: -1,
      ease: "none"
    });

    gsap.to(neurons.rotation, {
      y: Math.PI * 2,
      duration: 12,
      repeat: -1,
      ease: "none"
    });

    // Pulse animation for neurons
    neurons.children.forEach((child, i) => {
      if (child instanceof THREE.Mesh) {
        gsap.to(child.material, {
          emissiveIntensity: 0.2,
          duration: 1 + Math.random(),
          repeat: -1,
          yoyo: true,
          ease: "power1.inOut",
          delay: i * 0.02
        });
      }
    });

    // Animation loop
    const animate = () => {
      frame = requestAnimationFrame(animate);
      renderer.render(scene, camera);
    };

    animate();

    // Cleanup
    return () => {
      cancelAnimationFrame(frame);
      renderer.dispose();
      scene.clear();
      window.removeEventListener('resize', updateSize);
      if (container && container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <div className="flex flex-col items-center justify-center gap-4 w-full h-full">
      <div 
        ref={containerRef} 
        className="w-full max-w-[400px] h-[min(300px,40vh)] md:w-[400px] md:h-[300px]"
      />
      <p className="text-blue-400 animate-pulse font-medium text-sm md:text-base">
        Analyzing market data...
      </p>
    </div>
  );
};
